import { get } from 'lodash';
import uuid from 'uuid';

import { IDispatch, IRootState } from '../stores';
import { delay, postMessage } from '../../helpers';
import { IState, Locale, IURLParams, IMe, IViewResponse } from './types';
import {
  getWorkspaceInfo,
  getFirstLevelTable,
  getTable,
  getBatchedTables,
  getDefaultGridViewId,
  isDisabledOrDeletedSyncTable,
} from './helpers';
import { PostMessageEvent } from '../../types/message';
import { setIdsToLocalStorage, getIdsFromLocalStorage } from '../../helpers/storage';
import { createAnonymousUser, SHARE_USER_ID } from '../../helpers/user';

const getInitialState = (): IState => ({
  // Initial loading.
  isLoading: true,
  // User has changed the view from the dropdown.
  isLoadingView: false,
  // User just came in and is loading table options.
  isLoadingTableOptions: false,
  isViewEmpty: false,
  isExpand: false,
  canEdit: false,
  me: {
    id: '',
  },
  // User workspaceId. This will not change.
  currentWorkspaceId: '',
  // User tableId. This can change when user switches templates.
  currentTableId: '',
  // User viewId. This can change within the view. Changing this requires a GET.
  currentViewId: '',
  // User recordId. This can change within the view. Changing this doesn't require GET
  currentRecordId: '',
  currentBlockId: '',
  viewById: {},
  tableById: {},
  recordById: {},
  fieldById: {},
  // These are for rendering on the template list page (homepage);
  tableOptionById: {},
  tableOptionsAllIds: [],
  viewOptionById: {},
  visibleNodes: {},
  locale: Locale.ZH,
  users: {},
  toast: null,
  // Any errors that appear. This is probably a message.
  error: null,
  exitType: null,
});

const model = {
  state: getInitialState(),
  reducers: {
    setIsExpand(state: IState, payload: boolean) {
      return { ...state, isExpand: payload };
    },
    setIsViewEmpty(state: IState, payload: boolean) {
      return { ...state, isViewEmpty: payload };
    },
    setCanEdit(state: IState, payload: boolean) {
      return { ...state, canEdit: payload };
    },
    setMe(state: IState, payload: IMe) {
      return { ...state, me: { ...state.me, ...payload } };
    },
    setWorkspaceData(state: IState, payload: any) {
      return { ...state, ...payload };
    },
    setInitialData(state: IState, payload: any) {
      return { ...state, ...payload };
    },
    setInitialOptionsData(state: IState, payload: any) {
      return { ...state, ...payload };
    },
    setIncomingData(state: IState, payload: any) {
      return {
        ...state,
        fieldById: {
          ...state.fieldById,
          ...payload.fieldById,
        },
        recordById: {
          ...state.recordById,
          ...payload.recordById,
        },
        tableById: {
          ...state.tableById,
          ...payload.tableById,
        },
        viewById: {
          ...state.viewById,
          ...payload.viewById,
        },
      };
    },
    setLocale(state: IState, payload: Locale) {
      return { ...state, locale: payload };
    },
    setLoading(state: IState, payload: any) {
      return { ...state, isLoading: payload };
    },
    setLoadingView(state: IState, payload: any) {
      return { ...state, isLoadingView: payload };
    },
    setLoadingTableOptions(state: IState, isLoading: boolean) {
      return { ...state, isLoadingTableOptions: isLoading };
    },
    setCurrentData(state: IState, payload: any) {
      return { ...state, ...payload };
    },
    setCurrentRecord(state: IState, payload: string) {
      return { ...state, currentRecordId: payload };
    },
    setCurrentView(state: IState, payload: string) {
      return { ...state, currentViewId: payload };
    },
    setCurrentBlock(state: IState, payload: string) {
      return { ...state, currentBlockId: payload };
    },
    setCurrentWorkspace(state: IState, payload: string) {
      return { ...state, currentWorkspaceId: payload };
    },
    directlySetToast(state: IState, toast: NonNullable<IState['toast']>) {
      return { ...state, toast };
    },
    clearToast(state: IState) {
      return { ...state, toast: null };
    },
    setError(state: IState, payload: any) {
      return { ...state, error: payload };
    },
    setExitType(state: IState, payload: string | null) {
      return { ...state, exitType: payload };
    },
  },
  effects: (dispatch: IDispatch) => ({
    async getAllData({ urlParams }: { urlParams: IURLParams }) {
      const {
        core: { getWorkspace, getInitialTable },
        space: { getSpaceConfigs, getInitialTablesFromSpaces },
      } = dispatch;

      // Data from workspace includes the user list needed for collab field
      await getWorkspace({ workspaceId: urlParams.workspaceId });

      // The accum is the accumulated table data. It will be passed to getInitialTablesFromSpaces
      // once the configs for file creator are loaded. We will use the configs to check if any
      // record reference spaces need data from any other tables and add that data to the accum
      const { initPayloadWithoutAccum, accum } = await getInitialTable({ urlParams });

      // Set the initial data here so the user can see all non-record reference spaces
      // The record reference spaces will show a load spinner controlled by isLoadingTableData
      // This step is skipped in refreshData because it would cause the record reference spaces
      // to temporarily display partial data since no load spinners are shown during a refresh
      dispatch.core.setInitialData({
        ...initPayloadWithoutAccum,
        ...accum,
      });

      // Get the configs for file creator.
      await getSpaceConfigs({ tableId: urlParams.tableId });

      // Get table data needed for any record reference fields.
      const newAccum = await getInitialTablesFromSpaces({ accum });

      // Update the data so all record reference spaces can be rendered as well
      dispatch.core.setIncomingData(newAccum);
      dispatch.space.setLoadingTableData(false);

      // The global loading state that is set to true by default, mainly used to tell client
      // that loading is done so the block view loading animation can stop
      dispatch.core.setLoading(false);
    },
    // For pulling new table data, not file creator configs (see refreshAppConfig)
    // See getAllData above for detailed comments
    async refreshData(
      {
        urlParams,
      }: {
        urlParams: IURLParams;
      },
      { core }: IRootState,
    ) {
      // @TODO refactor how IDs are passed around
      // some IDs could probably be moved from urlParams to state for consistency
      if (!core.currentTableId) return;

      const {
        core: { getWorkspace, getInitialTable },
        space: { getInitialTablesFromSpaces },
      } = dispatch;

      await getWorkspace({ workspaceId: urlParams.workspaceId, isRefresh: true });
      const { initPayloadWithoutAccum, accum } = await getInitialTable({
        urlParams,
        isRefresh: true,
      });

      const newAccum = await getInitialTablesFromSpaces({ accum, isRefresh: true });

      dispatch.core.setInitialData({
        ...initPayloadWithoutAccum,
        ...newAccum,
      });
    },
    // For updating the configs for file creator
    async refreshAppConfig(_: undefined, { core }: IRootState) {
      const {
        space: { getSpaceConfigs },
      } = dispatch;

      await getSpaceConfigs({ tableId: core.currentTableId });
    },
    // get the nodes the user has permission to see
    // get the list of user ids for collaborators field
    async getWorkspace({ workspaceId, isRefresh }: { workspaceId: string; isRefresh?: boolean }) {
      if (!isRefresh) dispatch.core.setLoading(true);

      try {
        const { users, ...otherData } = await getWorkspaceInfo(workspaceId);

        const usersWithAnon = {
          ...users,
          [SHARE_USER_ID]: createAnonymousUser(),
        };

        dispatch.core.setWorkspaceData({ ...otherData, users: usersWithAnon });
      } catch (err) {
        console.warn(`An error occured in getWorkspace: ${err}`);
        dispatch.core.setError({ err: err });
      }
    },
    // on initial load of file creator, get data for the top level table
    async getInitialTable(
      {
        urlParams,
        isRefresh,
      }: {
        urlParams: IURLParams;
        isRefresh?: boolean;
      },
      { core }: IRootState,
    ) {
      const tableDataIds = {
        workspaceId: urlParams.workspaceId,
        tableId: urlParams.tableId,
        blockId: urlParams.blockId,
      };

      try {
        const initialAccum = { viewById: {}, tableById: {}, recordById: {}, fieldById: {} };
        const { allAdaptedViews, allAdaptedOptions } = await getFirstLevelTable({
          tableDataIds,
          accum: initialAccum,
        });

        if (!isRefresh) {
          postMessage({
            eventName: PostMessageEvent.CHECK_TABLE_EXISTENCE,
            data: {
              isTableExistence: true,
              isExpand: core.isExpand,
              blockId: urlParams.blockId,
            },
          });
        }

        // File creator opens to a record in a given view. Here, the view and record IDs
        // are validated against the data from back-end in case they are out of sync.
        const storedIds = getIdsFromLocalStorage(urlParams.blockId);
        let viewId: string;

        // The priority of view ID source is: local storage > URL params > default
        // Basically, the first time a file creator block is opened, the URL param view
        // ID is used to set local storage. From then on, file creator will always
        // prefer the local storage view ID as the source. The user can set this value
        // through the dropdown in the top navbar.
        if (storedIds && storedIds.viewId && allAdaptedViews.viewById[storedIds.viewId]) {
          // Use the view ID from local storage if it exists
          viewId = storedIds.viewId;
        } else if (urlParams.viewId && allAdaptedViews.viewById[urlParams.viewId]) {
          // Use the view ID provided in URL if it exists
          viewId = urlParams.viewId;
        } else {
          viewId = getDefaultGridViewId({ allAdaptedViews, allAdaptedOptions }, urlParams.tableId);
        }

        const recordsAllIds = allAdaptedViews.viewById[viewId].recordsAllIds || [];
        let recordId = recordsAllIds[0];

        if (storedIds && !(recordsAllIds.indexOf(storedIds.recordId) === -1)) {
          // Use the record ID from local storage if it exists
          recordId = storedIds.recordId;
        }

        // At this point we have validated the view and record IDs with the latest table
        // data. We want to update local storage with the these IDs.
        setIdsToLocalStorage({ blockId: urlParams.blockId, recordId, viewId });

        const initPayloadWithoutAccum = {
          ...allAdaptedOptions,
          currentRecordId: recordId,
          currentWorkspaceId: urlParams.workspaceId,
          currentCoreId: urlParams.coreId,
          currentTableId: urlParams.tableId,
          currentViewId: viewId,
          isViewEmpty: !recordId,
          isLoading: core.isExpand || isRefresh ? false : true,
        };

        return { initPayloadWithoutAccum, accum: allAdaptedViews };
      } catch (err) {
        console.error(err);
        postMessage({
          eventName: PostMessageEvent.CHECK_TABLE_EXISTENCE,
          data: {
            isTableExistence: false,
            isExpand: core.isExpand,
            blockId: urlParams.blockId,
          },
        });

        const initPayloadWithoutAccum = {
          tableOptionById: { 0: { viewOptionsAllIds: [0] } },
          viewOptionById: { 0: { id: 0, name: 'empty' } },
          currentTableId: 0,
          currentViewId: 0,
          isViewEmpty: true,
          isLoading: false,
          canEdit: false,
        };

        const accum = {
          viewById: {},
          tableById: {},
          recordById: {},
          fieldById: {},
        };

        dispatch.core.setError({ msgId: 'core.error.noTable' });
        return { initPayloadWithoutAccum, accum };
      }
    },
    // get data required to populate existing spaces on initial load
    async getBatchedTablesForSpace(
      payload: {
        tableSet: string[];
        accum: IViewResponse;
      },
      { core }: IRootState,
    ) {
      const tableParams = {
        workspaceId: core.currentWorkspaceId,
        blockId: core.currentBlockId || '',
        ...payload,
      };

      try {
        return await getBatchedTables(tableParams);
      } catch (err) {
        console.warn(`An error occured in getBatchedTablesForSpace: ${err}`);
        dispatch.core.setError({ err: err });
      }
    },
    // get data required to add a new column to space that references a foreign table
    async getTableForSpace(tableId: string, { core }: IRootState) {
      const node = core.visibleNodes[tableId];
      if (!node || isDisabledOrDeletedSyncTable(node.syncTableMeta)) return null;

      const accum = { viewById: {}, tableById: {}, recordById: {}, fieldById: {} };
      const tableParams = {
        workspaceId: core.currentWorkspaceId,
        tableId,
        blockId: core.currentBlockId || undefined,
      };

      try {
        return await getTable({ tableParams, accum });
      } catch (err) {
        console.warn(`An error occured in getTableForSpace: ${err}`);
        // Sets the record reference space to empty since there is no data
        return null;
      }
    },
    async changeView(payload: any, { core }: IRootState) {
      /**
       * When we change view, we need to check if the record exists inside this new view.
       * If it does, then we continue. If it doesn't then we redirect to a different view.
       */
      const recordIdsInNewView = core.viewById[payload].recordsAllIds || [];
      const recordExists = recordIdsInNewView.indexOf(core.currentRecordId) !== -1;
      const currentRecordId = recordExists ? core.currentRecordId : recordIdsInNewView[0];

      dispatch.core.setInitialData({
        isViewEmpty: recordIdsInNewView.length === 0,
        currentViewId: payload,
        currentRecordId,
      });

      setIdsToLocalStorage({
        blockId: core.currentBlockId,
        recordId: currentRecordId,
        viewId: payload,
      });
    },
    async changeRecord(payload: any, { core }: IRootState) {
      const allRecords = get(core, `viewById[${core.currentViewId}].recordsAllIds`);
      if (!allRecords) return;

      const currentIdx = allRecords.findIndex((id: string) => id === core.currentRecordId);
      if (currentIdx === undefined) return;
      const nextIdx = Math.max(0, Math.min(currentIdx + payload, allRecords.length - 1));

      dispatch.core.changeRecordById(allRecords[nextIdx]);
    },
    changeRecordById(payload: string, { core }: IRootState) {
      dispatch.core.setCurrentRecord(payload);
      dispatch.space.unfoldShadowClones();

      setIdsToLocalStorage({
        blockId: core.currentBlockId,
        recordId: payload,
        viewId: core.currentViewId,
      });
    },
    // Synchronize app view and record IDs from local storage
    syncAppView(_: undefined, { core }: IRootState) {
      if (core.isExpand) return;

      const storedIds = getIdsFromLocalStorage(core.currentBlockId);
      if (!storedIds) return;

      const { viewId, recordId } = storedIds;
      let view = core.viewById[core.currentViewId];

      if (viewId && core.viewById[viewId]) {
        dispatch.core.setCurrentView(viewId);
        view = core.viewById[viewId];
      }

      if (view?.recordsAllIds?.length) {
        if (recordId && view.recordsAllIds.indexOf(recordId) !== -1) {
          dispatch.core.setCurrentRecord(recordId);
        } else {
          dispatch.core.setCurrentRecord(view.recordsAllIds[0]);
        }
        dispatch.core.setIsViewEmpty(false);
      } else {
        dispatch.core.setCurrentRecord('');
        dispatch.core.setIsViewEmpty(true);
      }
    },
    setToast: (() => {
      let requestId = '';

      return async (
        payload: { toast: NonNullable<IState['toast']>; persist?: boolean },
        { core }: IRootState,
      ) => {
        const currentReuqestId = uuid();
        requestId = currentReuqestId;

        if (core.toast !== null) {
          dispatch.core.clearToast();

          await delay(10);
          if (currentReuqestId !== requestId) {
            return;
          }
        }

        dispatch.core.directlySetToast(payload.toast);

        if (payload.persist) {
          return;
        }

        await delay(5000);
        if (currentReuqestId !== requestId) {
          // If a new toast has started while we were waiting, we don't want
          // to clear anymore - that would clear the new one which is
          // responsible for it's own clearing.
          return;
        }

        dispatch.core.clearToast();
      };
    })(),
  }),
};

export default model;
